import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/auth';
import {
    Users,
    Check,
    X,
    Loader2,
    AlertCircle,
    Target,
    Mail,
    ArrowRight,
} from 'lucide-react';
import clsx from 'clsx';

export default function JoinWorkspacePage() {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();
    const user = useAuthStore((s) => s.user);
    const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

    const [joinStatus, setJoinStatus] = useState<'pending' | 'success' | 'error'>('pending');

    // Fetch invitation details
    const { data: invitationData, isLoading, error: fetchError } = useQuery({
        queryKey: ['invitation', token],
        queryFn: () => api.getInvitationDetails(token!),
        enabled: !!token,
        retry: false,
    });

    // Accept invitation mutation
    const acceptMutation = useMutation({
        mutationFn: () => api.acceptInvitation(token!),
        onSuccess: async (data) => {
            setJoinStatus('success');

            // Fetch the updated workspace list and set the new workspace as active

            // Fetch the updated workspace list and set the new workspace as active
            try {
                const workspaces = await api.getWorkspaces();
                const newWorkspace = workspaces.workspaces?.find(w => w.id === data.workspaceId);
                if (newWorkspace) {
                    // Set the new workspace as active
                    useAuthStore.getState().setWorkspace({
                        id: newWorkspace.id,
                        name: newWorkspace.name,
                        slug: newWorkspace.slug || '',
                    });
                }
            } catch (e) {
                console.error('Failed to update workspace after join:', e);
            }

            // Redirect to dashboard after 2 seconds
            setTimeout(() => {
                window.location.href = '/dashboard'; // Full refresh to clear all cache
            }, 2000);
        },
        onError: () => {
            setJoinStatus('error');
        },
    });

    const invitation = invitationData?.invitation;
    const errorMessage = fetchError ? (fetchError as Error).message : acceptMutation.error ? (acceptMutation.error as Error).message : null;

    // If not authenticated, show login prompt
    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Target className="w-8 h-8 text-primary-600" />
                    </div>

                    <h1 className="text-2xl font-bold mb-2">
                        {t('join.loginRequired', 'Đăng nhập để tiếp tục')}
                    </h1>

                    <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                        {t('join.loginMessage', 'Bạn cần đăng nhập hoặc tạo tài khoản để tham gia workspace.')}
                    </p>

                    <div className="space-y-3">
                        <Link
                            to={`/login?redirect=/join/${token}`}
                            className="btn-primary w-full gap-2"
                        >
                            <Mail className="w-4 h-4" />
                            {t('auth.login', 'Đăng nhập')}
                        </Link>
                        <Link
                            to={`/register?redirect=/join/${token}`}
                            className="btn-secondary w-full"
                        >
                            {t('auth.register', 'Tạo tài khoản mới')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-12 h-12 animate-spin text-primary-600 mx-auto mb-4" />
                    <p className="text-neutral-600 dark:text-neutral-400">
                        {t('join.loading', 'Đang tải thông tin lời mời...')}
                    </p>
                </div>
            </div>
        );
    }

    // Error fetching invitation
    if (fetchError || !invitation) {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
                    <div className="w-16 h-16 bg-danger-100 dark:bg-danger-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <AlertCircle className="w-8 h-8 text-danger-600" />
                    </div>

                    <h1 className="text-2xl font-bold mb-2 text-danger-600">
                        {t('join.invalid', 'Lời mời không hợp lệ')}
                    </h1>

                    <p className="text-neutral-600 dark:text-neutral-400 mb-6">
                        {errorMessage || t('join.invalidMessage', 'Lời mời này có thể đã hết hạn hoặc đã được sử dụng.')}
                    </p>

                    <Link to="/dashboard" className="btn-primary">
                        {t('join.goHome', 'Về trang chủ')}
                    </Link>
                </div>
            </div>
        );
    }

    // Success state
    if (joinStatus === 'success') {
        return (
            <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
                <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
                    <div className="w-16 h-16 bg-success-100 dark:bg-success-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Check className="w-8 h-8 text-success-600" />
                    </div>

                    <h1 className="text-2xl font-bold mb-2 text-success-600">
                        {t('join.success', 'Đã tham gia thành công!')}
                    </h1>

                    <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                        {t('join.successMessage', 'Chào mừng bạn đến với')} <strong>{invitation.workspaceName}</strong>
                    </p>

                    <p className="text-sm text-neutral-500 mb-6">
                        {t('join.redirecting', 'Đang chuyển hướng...')}
                    </p>

                    <Loader2 className="w-6 h-6 animate-spin text-primary-600 mx-auto" />
                </div>
            </div>
        );
    }

    // Invitation details
    return (
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
            <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl max-w-md w-full p-8">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="w-16 h-16 bg-primary-100 dark:bg-primary-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-8 h-8 text-primary-600" />
                    </div>

                    <h1 className="text-2xl font-bold mb-2">
                        {t('join.title', 'Lời mời tham gia workspace')}
                    </h1>
                </div>

                {/* Invitation Details */}
                <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-xl p-4 mb-6">
                    <div className="space-y-3">
                        <div>
                            <p className="text-sm text-neutral-500 mb-1">
                                {t('join.workspace', 'Workspace')}
                            </p>
                            <p className="font-semibold text-lg">{invitation.workspaceName}</p>
                        </div>

                        <div>
                            <p className="text-sm text-neutral-500 mb-1">
                                {t('join.invitedBy', 'Được mời bởi')}
                            </p>
                            <p className="font-medium">{invitation.invitedByName}</p>
                        </div>

                        <div>
                            <p className="text-sm text-neutral-500 mb-1">
                                {t('join.role', 'Vai trò của bạn')}
                            </p>
                            <p className="font-medium capitalize">
                                {invitation.role === 'member' ? 'Thành viên' : invitation.role === 'admin' ? 'Quản trị viên' : 'Người xem'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Email check */}
                <div className="mb-6 p-3 bg-blue-50 dark:bg-blue-600/10 rounded-lg text-sm">
                    <p className="text-blue-700 dark:text-blue-400">
                        {t('join.emailNote', 'Lời mời này được gửi đến')}: <strong>{invitation.email}</strong>
                    </p>
                    {user?.email?.toLowerCase() !== invitation.email.toLowerCase() && (
                        <p className="text-warning-600 mt-2">
                            {t('join.emailMismatch', 'Bạn đang đăng nhập với email khác. Vui lòng đăng nhập với email được mời.')}
                        </p>
                    )}
                </div>

                {/* Error state */}
                {joinStatus === 'error' && (
                    <div className="mb-6 p-3 bg-danger-50 dark:bg-danger-600/10 rounded-lg text-sm flex items-center gap-2 text-danger-600">
                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                        {errorMessage || t('join.error', 'Không thể tham gia workspace. Vui lòng thử lại.')}
                    </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                    <Link to="/dashboard" className="btn-secondary flex-1 gap-2">
                        <X className="w-4 h-4" />
                        {t('join.decline', 'Từ chối')}
                    </Link>
                    <button
                        onClick={() => acceptMutation.mutate()}
                        disabled={acceptMutation.isPending || user?.email?.toLowerCase() !== invitation.email.toLowerCase()}
                        className={clsx(
                            'btn-primary flex-1 gap-2',
                            user?.email?.toLowerCase() !== invitation.email.toLowerCase() && 'opacity-50 cursor-not-allowed'
                        )}
                    >
                        {acceptMutation.isPending ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <ArrowRight className="w-4 h-4" />
                        )}
                        {t('join.accept', 'Chấp nhận')}
                    </button>
                </div>
            </div>
        </div>
    );
}
